import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { db, storage, auth, functions } from '../../firebase';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, setDoc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { analyzeAndSanitize } from '../../utils/sanitizeHtml';

import './AdminPage.css';
import './EventDetailPage.css';
const RichTextEditorClassic = lazy(() => import('../RichTextEditorCdn'));
// const RichTextEditorClassic = lazy(() => import('../RichTextEditorClassic'));
const InvoiceGenerator = lazy(() => import('./InvoiceGenerator'));

// --- Date helpers for datetime-local ---
function toDatetimeLocalValue(date) {
  if (!(date instanceof Date) || isNaN(date)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const hh = pad(date.getHours());
  const mm = pad(date.getMinutes());
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

function fromDatetimeLocal(value) {
  if (!value) return null;
  const [datePart, timePart = '00:00'] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
}
// --- end helpers ---

// Convert any data:image base64 <img> tags in HTML to Firebase Storage URLs.
// CKEditor only routes images through the upload adapter when the Upload button
// is used; paste and drag embed images as base64 inline, which can push a
// Firestore document past its 1 MB size limit. Running this before save turns
// any embedded image into a normal URL regardless of how it got into the editor.
async function uploadEmbeddedImages(html) {
  if (!html || typeof html !== 'string') return html;
  if (!html.includes('data:image')) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const embeddedImgs = doc.querySelectorAll('img[src^="data:image"]');
  if (embeddedImgs.length === 0) return html;

  for (const img of embeddedImgs) {
    const src = img.getAttribute('src') || '';
    const match = src.match(/^data:image\/([a-zA-Z0-9+]+);base64,(.+)$/);
    if (!match) continue;

    const ext = match[1].toLowerCase().replace('jpeg', 'jpg');
    const base64 = match[2];

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: `image/${ext === 'jpg' ? 'jpeg' : ext}` });

    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const fileRef = ref(storage, `events/inline/${fileName}`);
    await uploadBytes(fileRef, blob, { contentType: blob.type });
    const url = await getDownloadURL(fileRef);
    img.setAttribute('src', url);
  }

  return doc.body.innerHTML;
}


function AdminPage() {
  const { isAdmin, currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out. Please try again.');
    }
  };

  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contactMessages, setContactMessages] = useState([]);
  const [contactFilter, setContactFilter] = useState('new');
  const [resendingOrderId, setResendingOrderId] = useState(null);

  // Re-send an order's tickets. Sends to the customer with cc Robbie/Elsa and
  // bcc Bróna. Changing the pre-filled address (e.g. to your own, to test, or
  // to a corrected customer address) sends only there, with no cc/bcc.
  const handleResendTickets = async (order) => {
    const recipient = window.prompt(
      `Re-send tickets for order #${order.orderNumber} to:\n\n` +
      '(Leave as-is to send to the customer with cc/bcc. ' +
      'Change the address to send a test copy only there.)',
      order.customerEmail
    );
    if (recipient === null || !recipient.trim()) {
      return;
    }
    setResendingOrderId(order.id);
    try {
      const resendFn = httpsCallable(functions, 'resendOrderTickets');
      const overrideEmail = recipient.trim() !== order.customerEmail ? recipient.trim() : undefined;
      const result = await resendFn({ orderId: order.id, ...(overrideEmail ? { overrideEmail } : {}) });
      if (result.data?.success) {
        alert(result.data.message || 'Tickets re-sent successfully.');
      } else {
        alert(result.data?.message || 'Could not re-send tickets.');
      }
    } catch (error) {
      console.error('Error re-sending tickets:', error);
      alert('Failed to re-send tickets: ' + error.message);
    } finally {
      setResendingOrderId(null);
    }
  };

  // Print door list for a single specific event
  const printSingleEventDoorList = (eventName, attendees) => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${eventName} - Door List</title>
        <style>
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          h1 {
            font-size: 24px;
            margin-bottom: 10px;
            color: #191919;
          }
          h2 {
            font-size: 20px;
            margin-top: 10px;
            margin-bottom: 15px;
            color: #191919;
          }
          .header-info {
            margin-bottom: 30px;
            color: #666;
            border-bottom: 2px solid #191919;
            padding-bottom: 15px;
          }
          .checklist-item {
            display: flex;
            align-items: center;
            padding: 10px 0;
            border-bottom: 1px solid #eee;
            page-break-inside: avoid;
          }
          .checklist-item:hover {
            background-color: #f5f5f5;
          }
          .checkbox {
            width: 24px;
            height: 24px;
            border: 2px solid #191919;
            margin-right: 15px;
            flex-shrink: 0;
          }
          .attendee-info {
            flex-grow: 1;
            display: flex;
            justify-content: space-between;
          }
          .attendee-name {
            font-weight: bold;
            font-size: 16px;
          }
          .order-number {
            color: #666;
            font-size: 14px;
          }
          .member-badge {
            background-color: #4CAF50;
            color: white;
            padding: 2px 8px;
            border-radius: 3px;
            font-size: 12px;
            margin-left: 10px;
          }
          .stats {
            margin: 20px 0;
            padding: 15px;
            background-color: #f5f5f5;
            border-radius: 5px;
          }
          .print-button {
            padding: 10px 20px;
            background-color: #191919;
            color: white;
            border: none;
            cursor: pointer;
            font-size: 16px;
            margin-bottom: 20px;
          }
          .print-button:hover {
            background-color: #333;
          }
        </style>
      </head>
      <body>
        <button class="print-button no-print" onclick="window.print()">Print This List</button>
        
        <h1>EVENT DOOR CHECKLIST</h1>
        <h2>${eventName}</h2>
        
        <div class="header-info">
          <p><strong>Print Date:</strong> ${currentDate}</p>
          <p><strong>Total Attendees:</strong> ${attendees.length}</p>
        </div>

        <div class="stats">
          <strong>Event:</strong> ${eventName}<br>
          <strong>Attendees to check in:</strong> ${attendees.length}
        </div>

        ${attendees.map((attendee, index) => `
          <div class="checklist-item">
            <div class="checkbox"></div>
            <div class="attendee-info">
              <div>
                <span class="attendee-name">${index + 1}. ${attendee.customerName}</span>
                ${attendee.isMember ? '<span class="member-badge">MEMBER</span>' : ''}
              </div>
              <span class="order-number">Order #${attendee.orderNumber}</span>
            </div>
          </div>
        `).join('')}
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Print door list function for all events
  const printDoorList = (orders) => {
    // Filter orders that have tickets (event purchases)
    const ticketOrders = orders.filter(order => 
      order.items && order.items.some(item => item.eventId || item.isEvent)
    );

    // Create a print window
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Group orders by event
    const ordersByEvent = {};
    ticketOrders.forEach(order => {
      order.items.forEach(item => {
        if (item.eventId || item.isEvent) {
          const eventName = item.name || 'Unknown Event';
          if (!ordersByEvent[eventName]) {
            ordersByEvent[eventName] = [];
          }
          
          // Add entry for each quantity
          for (let i = 0; i < (item.quantity || 1); i++) {
            ordersByEvent[eventName].push({
              customerName: order.customerName || order.customerEmail || 'Unknown',
              orderNumber: order.orderNumber || 'N/A',
              ticketQuantity: item.quantity || 1,
              price: item.totalPrice || item.price || 0,
              isMember: order.isMemberOrder || false,
              notes: order.dietaryNotes || order.notes || ''
            });
          }
        }
      });
    });

    // Generate HTML for printing
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Door List - ${currentDate}</title>
        <style>
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          h1 {
            font-size: 24px;
            margin-bottom: 10px;
            color: #191919;
          }
          h2 {
            font-size: 18px;
            margin-top: 30px;
            margin-bottom: 15px;
            color: #191919;
            border-bottom: 2px solid #191919;
            padding-bottom: 5px;
          }
          .header-info {
            margin-bottom: 30px;
            color: #666;
          }
          .checklist-item {
            display: flex;
            align-items: center;
            padding: 8px 0;
            border-bottom: 1px solid #eee;
            page-break-inside: avoid;
          }
          .checklist-item:hover {
            background-color: #f5f5f5;
          }
          .checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid #191919;
            margin-right: 15px;
            flex-shrink: 0;
          }
          .attendee-info {
            flex-grow: 1;
            display: flex;
            justify-content: space-between;
          }
          .attendee-name {
            font-weight: bold;
          }
          .order-number {
            color: #666;
            font-size: 14px;
          }
          .member-badge {
            background-color: #4CAF50;
            color: white;
            padding: 2px 8px;
            border-radius: 3px;
            font-size: 12px;
            margin-left: 10px;
          }
          .stats {
            margin-top: 20px;
            padding: 15px;
            background-color: #f5f5f5;
            border-radius: 5px;
          }
          .print-button {
            padding: 10px 20px;
            background-color: #191919;
            color: white;
            border: none;
            cursor: pointer;
            font-size: 16px;
            margin-bottom: 20px;
          }
          .print-button:hover {
            background-color: #333;
          }
        </style>
      </head>
      <body>
        <button class="print-button no-print" onclick="window.print()">Print This List</button>
        
        <h1>EVENT DOOR CHECKLIST</h1>
        <div class="header-info">
          <p><strong>Date:</strong> ${currentDate}</p>
          <p><strong>Total Events:</strong> ${Object.keys(ordersByEvent).length}</p>
          <p><strong>Total Attendees:</strong> ${ticketOrders.reduce((sum, order) => 
            sum + order.items.reduce((itemSum, item) => 
              (item.eventId || item.isEvent) ? itemSum + (item.quantity || 1) : itemSum, 0), 0)}</p>
        </div>

        ${Object.entries(ordersByEvent).map(([eventName, attendees]) => `
          <h2>${eventName}</h2>
          <div class="stats">
            <strong>Total for this event:</strong> ${attendees.length} attendee(s)
          </div>
          ${attendees.map((attendee, index) => `
            <div class="checklist-item">
              <div class="checkbox"></div>
              <div class="attendee-info">
                <div>
                  <span class="attendee-name">${index + 1}. ${attendee.customerName}</span>
                  ${attendee.isMember ? '<span class="member-badge">MEMBER</span>' : ''}
                </div>
                <span class="order-number">Order #${attendee.orderNumber}</span>
                ${attendee.notes ? `<div style="color:#856404;font-size:0.85em;margin-top:2px;">Dietary/Notes: ${attendee.notes}</div>` : ''}
              </div>
            </div>
          `).join('')}
        `).join('')}

        ${Object.keys(ordersByEvent).length === 0 ? '<p>No event tickets found in orders.</p>' : ''}
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Print packing list for shop orders (non-event items)
  const printShopOrders = (orders) => {
    // Filter orders that have non-event items
    const shopOrders = orders.filter(order => 
      order.items && order.items.some(item => !item.eventId && !item.isEvent)
    );

    // Create a print window
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Generate HTML for printing
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Shop Orders Packing List - ${currentDate}</title>
        <style>
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
            .order-box { page-break-inside: avoid; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          h1 {
            font-size: 24px;
            margin-bottom: 10px;
            color: #191919;
          }
          .header-info {
            margin-bottom: 30px;
            color: #666;
            border-bottom: 2px solid #191919;
            padding-bottom: 15px;
          }
          .order-box {
            border: 2px solid #191919;
            padding: 20px;
            margin-bottom: 30px;
            border-radius: 5px;
            background-color: #fafafa;
          }
          .order-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 1px solid #ddd;
          }
          .order-number {
            font-size: 18px;
            font-weight: bold;
            color: #191919;
          }
          .order-date {
            color: #666;
            font-size: 14px;
          }
          .customer-section {
            margin-bottom: 20px;
            padding: 10px;
            background-color: white;
            border-left: 4px solid #191919;
          }
          .customer-section h3 {
            margin-top: 0;
            margin-bottom: 10px;
            color: #191919;
          }
          .checkbox-item {
            display: flex;
            align-items: center;
            padding: 8px 0;
            border-bottom: 1px dotted #ddd;
          }
          .checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid #191919;
            margin-right: 15px;
            flex-shrink: 0;
          }
          .item-details {
            flex-grow: 1;
            display: flex;
            justify-content: space-between;
          }
          .item-name {
            font-weight: bold;
          }
          .item-quantity {
            color: #666;
            margin-left: 10px;
          }
          .member-badge {
            background-color: #4CAF50;
            color: white;
            padding: 2px 8px;
            border-radius: 3px;
            font-size: 12px;
            margin-left: 10px;
          }
          .print-button {
            padding: 10px 20px;
            background-color: #191919;
            color: white;
            border: none;
            cursor: pointer;
            font-size: 16px;
            margin-bottom: 20px;
          }
          .print-button:hover {
            background-color: #333;
          }
          .stats {
            background-color: #f5f5f5;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 20px;
          }
        </style>
      </head>
      <body>
        <button class="print-button no-print" onclick="window.print()">Print This List</button>
        
        <h1>SHOP ORDERS - PACKING LIST</h1>
        <div class="header-info">
          <p><strong>Print Date:</strong> ${currentDate}</p>
          <p><strong>Total Orders to Pack:</strong> ${shopOrders.length}</p>
          <p><strong>Total Items:</strong> ${shopOrders.reduce((sum, order) => 
            sum + order.items.filter(item => !item.eventId && !item.isEvent)
                       .reduce((itemSum, item) => itemSum + (item.quantity || 1), 0), 0)}</p>
        </div>

        ${shopOrders.length === 0 ? '<p>No shop orders to pack.</p>' : ''}

        ${shopOrders.map(order => {
          const shopItems = order.items.filter(item => !item.eventId && !item.isEvent);
          const orderDate = order.ticketsGeneratedAt?.toDate?.() || 
                           order.orderDate?.toDate?.() || 
                           new Date();
          
          return `
            <div class="order-box">
              <div class="order-header">
                <div class="order-number">ORDER #${order.orderNumber || 'N/A'}</div>
                <div class="order-date">${orderDate.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</div>
              </div>
              
              <div class="customer-section">
                <h3>Customer Information</h3>
                <p><strong>Name:</strong> ${order.customerName || order.customerEmail || 'Unknown'}
                   ${order.isMemberOrder ? '<span class="member-badge">MEMBER</span>' : ''}
                </p>
                <p><strong>Email:</strong> ${order.customerEmail || 'N/A'}</p>
                ${order.shippingAddress ? `
                  <h3 style="margin-top: 15px;">Shipping Address</h3>
                  <p>${order.shippingAddress.address || ''}</p>
                  <p>${order.shippingAddress.city || ''}, ${order.shippingAddress.postalCode || ''}</p>
                  <p>${order.shippingAddress.country || ''}</p>
                ` : '<p><em>No shipping address provided</em></p>'}
                ${(order.dietaryNotes || order.notes) ? `<p style="margin-top:10px;padding:8px;background:#fff3cd;border-radius:4px;"><strong>Special Requirements:</strong> ${order.dietaryNotes || order.notes}</p>` : ''}
              </div>

              <h3>Items to Pack:</h3>
              ${shopItems.map(item => `
                <div class="checkbox-item">
                  <div class="checkbox"></div>
                  <div class="item-details">
                    <div>
                      <span class="item-name">${item.name || 'Unknown Item'}</span>
                      <span class="item-quantity">× ${item.quantity || 1}</span>
                    </div>
                    <div>
                      ${item.totalPrice ? `${item.totalPrice.toFixed(2)} SEK` : ''}
                    </div>
                  </div>
                </div>
              `).join('')}
              
              <div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd;">
                <p><strong>Total Amount:</strong> ${
                  order.finalTotal ? order.finalTotal.toFixed(2) : 
                  order.amount ? (order.amount / 100).toFixed(2) : 
                  order.items && order.items[0] && order.items[0].amount ? (order.items[0].amount / 100).toFixed(2) :
                  shopItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0).toFixed(2)
                } SEK</p>
                ${order.isMemberOrder && order.memberDiscount ? 
                  `<p style="color: #4CAF50;"><em>Member discount applied: -${order.memberDiscount.toFixed(2)} SEK</em></p>` 
                  : ''}
              </div>
            </div>
          `;
        }).join('')}
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Admin user form state
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: ''
  });

  // Event form state
  const [eventForm, setEventForm] = useState({
    title: '',
    subtitle: '',
    richDescription: '',
    title_sv: '',
    subtitle_sv: '',
    richDescription_sv: '',
    menuText_sv: '',
    menuHeading_sv: '',
    priceLabel_sv: '',
    price2Label_sv: '',
    price3Label_sv: '',
    price4Label_sv: '',
    price5Label_sv: '',
    date: '',
    endDate: '',
    price: '',
    memberPrice: '',
    priceLabel: '',
    price2: '',
    price2Label: '',
    price3: '',
    price3Label: '',
    price4: '',
    price4Label: '',
    price5: '',
    price5Label: '',
    menuText: '',
    menuHeading: '',
    category: 'music',
    imageUrl: '',
    thumbnailUrl: '',
    images: [],
    mainImageIndex: 0,
    status: 'upcoming',
    location: 'Courtyard'
  });
  const [showSwedish, setShowSwedish] = useState(false);

  // Edit mode state
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEventPreview, setShowEventPreview] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [recurringConfig, setRecurringConfig] = useState({
    frequency: 'weekly',
    startDate: '',
    endDate: '',
    creatingRecurring: false
  });

  // Product form state
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    richDescription: '',
    price: '',
    memberPrice: '',
    stock: '',
    category: 'tickets',
    type: 'physical',
    imageUrl: '',
    thumbnailUrl: '',
    images: [], // Array to store multiple image URLs
    mainImageIndex: 0, // Track which image is the main one
    inStock: true,
    downloadEnabled: false,
    downloadPrice: '',
    physicalLabel: '',
    downloadLabel: '',
    physicalIncludesDownload: false,
    downloadFilePath: '',
    downloadFileName: '',
    postage: {
      sweden: '',
      europe: '',
      worldwide: ''
    }
  });

  // YouTube form state
  const [youtubeForm, setYoutubeForm] = useState({
    url: '',
    title: '',
    description: ''
  });

  // File upload states
 
  const [eventImageFiles, setEventImageFiles] = useState([]); // Multiple event images
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImageFiles, setProductImageFiles] = useState([]); // Multiple product images
  const [carouselImageFile, setCarouselImageFile] = useState(null);

  const [uploadingEventImages, setUploadingEventImages] = useState(false); // Multiple images upload state
  const [uploadingProductImage, setUploadingProductImage] = useState(false);
  const [uploadingProductImages, setUploadingProductImages] = useState(false); // Multiple images upload state
  const [uploadingCarouselImage, setUploadingCarouselImage] = useState(false);
  const [eventThumbnailFile, setEventThumbnailFile] = useState(null);
  const [uploadingEventThumbnail, setUploadingEventThumbnail] = useState(false);
  const [productThumbnailFile, setProductThumbnailFile] = useState(null);
  const [uploadingProductThumbnail, setUploadingProductThumbnail] = useState(false);
  const [productDownloadFile, setProductDownloadFile] = useState(null);
  const [downloadUploadProgress, setDownloadUploadProgress] = useState(null);

  // Helper functions for image management
  const removeProductImage = (index) => {
    const newImages = [...productForm.images];
    newImages.splice(index, 1);
    setProductForm({
      ...productForm,
      images: newImages
    });
  };

  const removeEventImage = (index) => {
    const newImages = [...eventForm.images];
    newImages.splice(index, 1);
    setEventForm({
      ...eventForm,
      images: newImages
    });
  };

  useEffect(() => {
    if (!currentUser || !isAdmin()) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, isAdmin, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load events
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEvents(eventsData);

      // Load products
      const productsSnapshot = await getDocs(collection(db, 'products'));
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsData);

      // Load orders
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(ordersData.sort((a, b) => {
        // Check for either orderDate or ticketsGeneratedAt
        const aTime = (a.orderDate && a.orderDate.seconds) ? a.orderDate.seconds : 
                     (a.ticketsGeneratedAt && a.ticketsGeneratedAt.seconds) ? a.ticketsGeneratedAt.seconds : 0;
        const bTime = (b.orderDate && b.orderDate.seconds) ? b.orderDate.seconds : 
                     (b.ticketsGeneratedAt && b.ticketsGeneratedAt.seconds) ? b.ticketsGeneratedAt.seconds : 0;
        return bTime - aTime;
      }));

      // Load contact messages
      try {
        const contactSnapshot = await getDocs(query(collection(db, 'contactMessages'), orderBy('createdAt', 'desc')));
        const contactData = contactSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setContactMessages(contactData);
      } catch (err) {
        console.error('Error loading contact messages:', err);
      }

      // Load YouTube videos
      const youtubeSnapshot = await getDocs(collection(db, 'youtubeVideos'));
      const youtubeData = youtubeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setYoutubeVideos(youtubeData.sort((a, b) => {
        const aTime = a.createdAt && a.createdAt.seconds ? a.createdAt.seconds : 0;
        const bTime = b.createdAt && b.createdAt.seconds ? b.createdAt.seconds : 0;
        return bTime - aTime;
      }));

      // Load carousel images
      const carouselSnapshot = await getDocs(collection(db, 'carouselImages'));
      const carouselData = carouselSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCarouselImages(carouselData.sort((a, b) => {
        const aTime = a.createdAt && a.createdAt.seconds ? a.createdAt.seconds : 0;
        const bTime = b.createdAt && b.createdAt.seconds ? b.createdAt.seconds : 0;
        return aTime - bTime; // Oldest first for display order
      }));

      // Load admin users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const adminUsersData = usersSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(user => user.role === 'admin');
      setAdminUsers(adminUsersData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };



  // Upload multiple event images
const uploadEventImages = async (files = eventImageFiles) => {
  if (!files || files.length === 0) return [];

  setUploadingEventImages(true);
  const uploadedUrls = [];

  try {
    if (!currentUser) throw new Error('User must be authenticated to upload images');

    for (const file of files) {
      const sanitizedFileName = file.name
        .replace(/[^a-zA-Z0-9.-]/g, '_')
        .replace(/\s+/g, '_')
        .toLowerCase();

      const imageRef = ref(storage, `events/${Date.now()}_${sanitizedFileName}`);
      await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(imageRef);
      uploadedUrls.push(downloadURL);
    }
  } catch (err) {
    console.error('Error uploading event images:', err);
    alert(err.message || 'Error uploading images');
  } finally {
    setUploadingEventImages(false);
  }

  return uploadedUrls;
};


  const uploadProductImage = async () => {
    if (!productImageFile) return null;
    
    setUploadingProductImage(true);
    try {
      // Check if user is authenticated
      if (!currentUser) {
        throw new Error('User must be authenticated to upload images');
      }

      // Sanitize filename to avoid 400 errors
      const sanitizedFileName = productImageFile.name
        .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace special chars with underscore
        .replace(/\s+/g, '_') // Replace spaces with underscore
        .toLowerCase();

      const imageRef = ref(storage, `products/${Date.now()}_${sanitizedFileName}`);
      await uploadBytes(imageRef, productImageFile);
      const downloadURL = await getDownloadURL(imageRef);
      setUploadingProductImage(false);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading product image:', error);
      setUploadingProductImage(false);
      
      // Provide user-friendly error message
      if (error.code === 'storage/unauthorized') {
        alert('Error: You do not have permission to upload images. Please check Firebase Storage rules.');
      } else if (error.code === 'storage/unknown') {
        alert('Error: Firebase Storage service may not be enabled. Please enable Storage in Firebase Console.');
      } else {
        alert(`Error uploading image: ${error.message}`);
      }
      
      return null; // Return null instead of throwing to allow form submission without image
    }
  };

  // Upload multiple product images
  const uploadProductImages = async () => {
    if (!productImageFiles || productImageFiles.length === 0) return [];
    
    setUploadingProductImages(true);
    const uploadedUrls = [];
    
    try {
      // Check if user is authenticated
      if (!currentUser) {
        throw new Error('User must be authenticated to upload images');
      }

      for (const file of productImageFiles) {
        // Sanitize filename to avoid 400 errors
        const sanitizedFileName = file.name
          .replace(/[^a-zA-Z0-9.-]/g, '_')
          .replace(/\s+/g, '_')
          .toLowerCase();

        const imageRef = ref(storage, `products/${Date.now()}_${sanitizedFileName}`);
        await uploadBytes(imageRef, file);
        const downloadURL = await getDownloadURL(imageRef);
        uploadedUrls.push(downloadURL);
      }
      
      setUploadingProductImages(false);
      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading product images:', error);
      setUploadingProductImages(false);
      
      if (error.code === 'storage/unauthorized') {
        alert('Error: You do not have permission to upload images. Please check Firebase Storage rules.');
      } else if (error.code === 'storage/unknown') {
        alert('Error: Firebase Storage service may not be enabled. Please enable Storage in Firebase Console.');
      } else {
        alert(`Error uploading images: ${error.message}`);
      }
      
      return uploadedUrls; // Return any successfully uploaded URLs
    }
  };

  // Upload dedicated thumbnails (square) for events and products
  const uploadEventThumbnail = async () => {
    if (!eventThumbnailFile) return null;

    setUploadingEventThumbnail(true);
    try {
      if (!currentUser) {
        throw new Error('User must be authenticated to upload images');
      }

      const sanitizedFileName = eventThumbnailFile.name
        .replace(/[^a-zA-Z0-9.-]/g, '_')
        .replace(/\s+/g, '_')
        .toLowerCase();

      const imageRef = ref(storage, `thumbnails/events/${Date.now()}_${sanitizedFileName}`);
      await uploadBytes(imageRef, eventThumbnailFile);
      const downloadURL = await getDownloadURL(imageRef);
      setUploadingEventThumbnail(false);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading event thumbnail:', error);
      setUploadingEventThumbnail(false);
      alert(error.message || 'Error uploading event thumbnail');
      return null;
    }
  };

  const uploadProductThumbnail = async () => {
    if (!productThumbnailFile) return null;

    setUploadingProductThumbnail(true);
    try {
      if (!currentUser) {
        throw new Error('User must be authenticated to upload images');
      }

      const sanitizedFileName = productThumbnailFile.name
        .replace(/[^a-zA-Z0-9.-]/g, '_')
        .replace(/\s+/g, '_')
        .toLowerCase();

      const imageRef = ref(storage, `thumbnails/products/${Date.now()}_${sanitizedFileName}`);
      await uploadBytes(imageRef, productThumbnailFile);
      const downloadURL = await getDownloadURL(imageRef);
      setUploadingProductThumbnail(false);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading product thumbnail:', error);
      setUploadingProductThumbnail(false);
      alert(error.message || 'Error uploading product thumbnail');
      return null;
    }
  };

  const uploadCarouselImage = async () => {
    if (!carouselImageFile) return null;
    
    setUploadingCarouselImage(true);
    try {
      // Check if user is authenticated
      if (!currentUser) {
        throw new Error('User must be authenticated to upload images');
      }

      // Sanitize filename to avoid 400 errors
      const sanitizedFileName = carouselImageFile.name
        .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace special chars with underscore
        .replace(/\s+/g, '_') // Replace spaces with underscore
        .toLowerCase();

      const imageRef = ref(storage, `carousel/${Date.now()}_${sanitizedFileName}`);
      await uploadBytes(imageRef, carouselImageFile);
      const downloadURL = await getDownloadURL(imageRef);
      setUploadingCarouselImage(false);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading carousel image:', error);
      setUploadingCarouselImage(false);
      
      // Provide user-friendly error message
      if (error.code === 'storage/unauthorized') {
        alert('Error: You do not have permission to upload images. Please check Firebase Storage rules.');
      } else if (error.code === 'storage/unknown') {
        alert('Error: Firebase Storage service may not be enabled. Please enable Storage in Firebase Console.');
      } else {
        alert(`Error uploading image: ${error.message}`);
      }
      
      return null;
    }
  };

const handleEventSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    const newUploads = eventImageFiles.length ? await uploadEventImages(eventImageFiles) : [];

    const seen = new Set();
    const uniq = (arr) => arr.filter(u => u && !seen.has(u) && seen.add(u));

    const existing = Array.isArray(eventForm.images) ? eventForm.images : [];

    // Combine existing images with new uploads
    const allImages = editingEvent 
      ? uniq([...existing, ...newUploads])
      : uniq([...newUploads, ...existing]);

    // Upload any pasted/dropped base64 images to Firebase Storage first,
    // so the saved HTML only contains small URLs rather than huge data URIs.
    const richHtmlWithUrls = await uploadEmbeddedImages(eventForm.richDescription);
    const richHtmlSvWithUrls = await uploadEmbeddedImages(eventForm.richDescription_sv);

    // 🔒 Sanitize the rich description here
    const { html: cleanHtml, report } = analyzeAndSanitize(richHtmlWithUrls);

    const when = eventForm.date ? fromDatetimeLocal(eventForm.date) : null;

    // Determine thumbnail for event: prefer newly uploaded thumbnail, else use first image
    let thumb = '';
    if (eventThumbnailFile) {
      const uploadedThumb = await uploadEventThumbnail();
      if (uploadedThumb) thumb = uploadedThumb;
    } else if (eventForm.thumbnailUrl) {
      // Keep existing thumbnail if present
      thumb = eventForm.thumbnailUrl;
    } else if (allImages[0]) {
      // Auto-set thumbnail to first image if no thumbnail specified
      thumb = allImages[0];
    }

    const eventData = {
      title: eventForm.title,
      subtitle: eventForm.subtitle,
      // store the clean version for rendering
      richDescription: cleanHtml,
      // pre-sanitization copy for future editing/auditing (already has base64
      // images converted to Storage URLs to stay under Firestore's 1 MB limit)
      richDescriptionRaw: richHtmlWithUrls,
      richDescriptionReport: report,          // optional: helps you see hosts/tags used
      sanitizerPolicyVersion: 1,              // optional: version your policy

      category: eventForm.category,
      status: eventForm.status,
      imageUrl: allImages[eventForm.mainImageIndex || 0] || '',  // Use the selected main image
      images: allImages,
      mainImageIndex: eventForm.mainImageIndex || 0,  // Store which image is the main one
      thumbnailUrl: thumb,
      date: when, //no UTC conversion
      // Optional last day for multi-day events; null for single-day events
      endDate: eventForm.endDate ? fromDatetimeLocal(eventForm.endDate) : null,
      price: eventForm.price ? parseFloat(eventForm.price) : 0,
      memberPrice: eventForm.memberPrice ? parseFloat(eventForm.memberPrice) : 0,
      priceLabel: eventForm.priceLabel || '',
      price2: eventForm.price2 ? parseFloat(eventForm.price2) : 0,
      price2Label: eventForm.price2Label || '',
      price3: eventForm.price3 ? parseFloat(eventForm.price3) : 0,
      price3Label: eventForm.price3Label || '',
      price4: eventForm.price4 ? parseFloat(eventForm.price4) : 0,
      price4Label: eventForm.price4Label || '',
      price5: eventForm.price5 ? parseFloat(eventForm.price5) : 0,
      price5Label: eventForm.price5Label || '',
      menuText: eventForm.menuText || '',
      menuHeading: eventForm.menuHeading || '',
      title_sv: eventForm.title_sv || '',
      subtitle_sv: eventForm.subtitle_sv || '',
      richDescription_sv: richHtmlSvWithUrls || '',
      menuText_sv: eventForm.menuText_sv || '',
      menuHeading_sv: eventForm.menuHeading_sv || '',
      priceLabel_sv: eventForm.priceLabel_sv || '',
      price2Label_sv: eventForm.price2Label_sv || '',
      price3Label_sv: eventForm.price3Label_sv || '',
      price4Label_sv: eventForm.price4Label_sv || '',
      price5Label_sv: eventForm.price5Label_sv || '',
      type: eventForm.category,
      createdAt: editingEvent ? editingEvent.createdAt : new Date(),
      createdBy: currentUser.uid,
      isEvent: true,
      eventId: editingEvent ? (editingEvent.eventId || editingEvent.id) : `EVT-${Date.now()}`,
      location: eventForm.location || 'Courtyard'
    };

    if (editingEvent) {
      await updateDoc(doc(db, 'events', editingEvent.id), eventData);
      // Stay on the event — update editingEvent with latest data
      setEditingEvent({ ...editingEvent, ...eventData });
      setEventImageFiles([]);
      setEventThumbnailFile(null);
      loadData();
      alert('Event updated successfully!');
    } else {
      await addDoc(collection(db, 'events'), eventData);
      // Reset form after creating a new event
      setShowSwedish(false);
      setEventForm({
        title: '',
        subtitle: '',
        richDescription: '',
        title_sv: '',
        subtitle_sv: '',
        richDescription_sv: '',
        menuText_sv: '',
        menuHeading_sv: '',
        priceLabel_sv: '',
        price2Label_sv: '',
        price3Label_sv: '',
        price4Label_sv: '',
        price5Label_sv: '',
        date: '',
        endDate: '',
        price: '',
        memberPrice: '',
        priceLabel: '',
        price2: '',
        price2Label: '',
        price3: '',
        price3Label: '',
        price4: '',
        price4Label: '',
        price5: '',
        price5Label: '',
        menuText: '',
        menuHeading: '',
        category: 'music',
        imageUrl: '',
        thumbnailUrl: '',
        images: [],
        mainImageIndex: 0,
        status: 'upcoming',
        location: 'Courtyard'
      });
      setEventImageFiles([]);
      setEventThumbnailFile(null);
      loadData();
      alert('Event created successfully!');
    }
  } catch (error) {
    console.error('Error saving event:', error);
    alert('Error saving event');
  } finally {
    setLoading(false);
  }
};



  // Upload the file sold as a digital download. It goes to the private
  // downloads/ folder — buyers only get it via a signed link after payment.
  const uploadProductDownloadFile = async () => {
    if (!productDownloadFile) return null;
    const sanitizedFileName = productDownloadFile.name
      .replace(/[^a-zA-Z0-9.-]/g, '_')
      .replace(/_{2,}/g, '_');
    const path = `downloads/${Date.now()}_${sanitizedFileName}`;
    const task = uploadBytesResumable(ref(storage, path), productDownloadFile);
    setDownloadUploadProgress(0);
    try {
      await new Promise((resolve, reject) => {
        task.on(
          'state_changed',
          (snap) => setDownloadUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          resolve
        );
      });
    } finally {
      setDownloadUploadProgress(null);
    }
    return { path, fileName: productDownloadFile.name };
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Upload new images if any
      const newUploads = productImageFiles.length ? await uploadProductImages() : [];
      
      // Get existing images
      const existing = Array.isArray(productForm.images) ? productForm.images : [];
      
      // Combine existing images with new uploads
      const allImages = editingProduct 
        ? [...existing, ...newUploads]
        : [...newUploads, ...existing];

      // Determine thumbnail for product: prefer newly uploaded thumbnail, else use main image
      let pthumb = '';
      if (productThumbnailFile) {
        const uploadedThumb = await uploadProductThumbnail();
        if (uploadedThumb) pthumb = uploadedThumb;
      } else if (productForm.thumbnailUrl) {
        // Keep existing thumbnail if present
        pthumb = productForm.thumbnailUrl;
      } else if (allImages[0]) {
        // Auto-set thumbnail to main image if no thumbnail specified
        pthumb = allImages[0];
      }

      let downloadFilePath = productForm.downloadFilePath || '';
      let downloadFileName = productForm.downloadFileName || '';
      if (productDownloadFile) {
        const uploaded = await uploadProductDownloadFile();
        downloadFilePath = uploaded.path;
        downloadFileName = uploaded.fileName;
      }
      if (productForm.downloadEnabled && (!downloadFilePath || !(parseFloat(productForm.downloadPrice) > 0))) {
        alert('Digital download is enabled — please add a download price and upload the download file.');
        setLoading(false);
        return;
      }

      // Sanitize the rich description
      const { html: cleanHtml, report } = analyzeAndSanitize(productForm.richDescription);

      const productData = {
        ...productForm,
        richDescription: cleanHtml,
        richDescriptionRaw: productForm.richDescription,
        richDescriptionReport: report,
        sanitizerPolicyVersion: 1,
        imageUrl: allImages[0] || '', // First image as fallback
        images: allImages, // All images
        thumbnailUrl: pthumb,
        price: parseFloat(productForm.price),
        memberPrice: parseFloat(productForm.memberPrice),
        stock: parseInt(productForm.stock),
        downloadPrice: productForm.downloadPrice ? parseFloat(productForm.downloadPrice) : null,
        downloadFilePath,
        downloadFileName,
        createdAt: editingProduct ? editingProduct.createdAt : new Date(),
        createdBy: currentUser.uid
      };
      
      if (editingProduct) {
        // Update existing product
        await updateDoc(doc(db, 'products', editingProduct.id), productData);
        setEditingProduct(null);
        alert('Product updated successfully!');
      } else {
        // Create new product
        await addDoc(collection(db, 'products'), productData);
        alert('Product created successfully!');
      }
      
      setProductForm({
        name: '',
        description: '',
        richDescription: '',
        price: '',
        memberPrice: '',
        stock: '',
        category: 'tickets',
        type: 'physical',
        imageUrl: '',
        thumbnailUrl: '',
        images: [],
        inStock: true,
        downloadEnabled: false,
        downloadPrice: '',
        physicalLabel: '',
        downloadLabel: '',
        physicalIncludesDownload: false,
        downloadFilePath: '',
        downloadFileName: '',
      });
      setProductImageFile(null);
      setProductDownloadFile(null);
      setProductImageFiles([]);
      setProductThumbnailFile(null);
      loadData();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product');
    }
    setLoading(false);
  };

  const editProduct = (product) => {
    setProductForm({
      name: product.name || '',
      description: product.description || '',
      richDescription: product.richDescriptionRaw || product.richDescription || '',
      price: product.price ? product.price.toString() : '',
      memberPrice: product.memberPrice ? product.memberPrice.toString() : '',
      stock: product.stock ? product.stock.toString() : '',
      category: product.category || 'tickets',
      type: product.type || 'physical',
      imageUrl: product.imageUrl || '',
      thumbnailUrl: product.thumbnailUrl || '',
      images: product.images || [],
      inStock: product.inStock !== false,
      downloadEnabled: !!product.downloadEnabled,
      downloadPrice: product.downloadPrice ? product.downloadPrice.toString() : '',
      physicalLabel: product.physicalLabel || '',
      downloadLabel: product.downloadLabel || '',
      physicalIncludesDownload: !!product.physicalIncludesDownload,
      downloadFilePath: product.downloadFilePath || '',
      downloadFileName: product.downloadFileName || '',
      postage: {
        sweden: product.postage?.sweden ? product.postage.sweden.toString() : '',
        europe: product.postage?.europe ? product.postage.europe.toString() : '',
        worldwide: product.postage?.worldwide ? product.postage.worldwide.toString() : ''
      }
    });
    setEditingProduct(product);
    setProductDownloadFile(null);
    setProductImageFile(null);
    setProductImageFiles([]);
    setProductThumbnailFile(null);
  };

  const cancelEditProduct = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      richDescription: '',
      price: '',
      memberPrice: '',
      stock: '',
      category: 'tickets',
      type: 'physical',
      imageUrl: '',
      thumbnailUrl: '',
      images: [],
      inStock: true,
      downloadEnabled: false,
      downloadPrice: '',
      physicalLabel: '',
      downloadLabel: '',
      physicalIncludesDownload: false,
      downloadFilePath: '',
      downloadFileName: '',
      postage: {
        sweden: '',
        europe: '',
        worldwide: ''
      }
    });
    setProductImageFile(null);
    setProductDownloadFile(null);
    setProductImageFiles([]);
    setProductThumbnailFile(null);
  };

const editEvent = (event) => {
  let dateValue = '';
  if (event.date) {
    const eventDate = event.date.toDate ? event.date.toDate() : new Date(event.date);
    dateValue = toDatetimeLocalValue(eventDate); // ← use local formatter, not toISOString
  }

  let endDateValue = '';
  if (event.endDate) {
    const eventEndDate = event.endDate.toDate ? event.endDate.toDate() : new Date(event.endDate);
    endDateValue = toDatetimeLocalValue(eventEndDate);
  }

  // Get the rich description, preferring raw version if available
  let richDescriptionToLoad = '';
  
  // First try richDescriptionRaw (original unsanitized content)
  if (event.richDescriptionRaw) {
    richDescriptionToLoad = event.richDescriptionRaw;
    console.log('Loading richDescriptionRaw for editing');
  } 
  // Fallback to richDescription if no raw version exists
  else if (event.richDescription) {
    richDescriptionToLoad = event.richDescription;
    console.log('Loading richDescription (no raw version available)');
  }
  
  // Clean up any potentially corrupted content
  if (richDescriptionToLoad) {
    // Remove any standalone closing divs that might cause issues
    richDescriptionToLoad = richDescriptionToLoad.replace(/<\/div>\s*$/, '');
    
    // If the content appears to be corrupted (empty paragraphs/divs), clear it
    if (richDescriptionToLoad === '<p></p>' || 
        richDescriptionToLoad === '<div></div>' ||
        richDescriptionToLoad === '<p>&nbsp;</p>') {
      console.log('Detected corrupted content, clearing editor');
      richDescriptionToLoad = '';
    }
  }

  setEventForm({
    title: event.title || '',
    subtitle: event.subtitle || '',
    richDescription: richDescriptionToLoad,
    title_sv: event.title_sv || '',
    subtitle_sv: event.subtitle_sv || '',
    richDescription_sv: event.richDescription_sv || '',
    menuText_sv: event.menuText_sv || '',
    menuHeading_sv: event.menuHeading_sv || '',
    priceLabel_sv: event.priceLabel_sv || '',
    price2Label_sv: event.price2Label_sv || '',
    price3Label_sv: event.price3Label_sv || '',
    price4Label_sv: event.price4Label_sv || '',
    price5Label_sv: event.price5Label_sv || '',
    date: dateValue,
    endDate: endDateValue,
    price: event.price ? event.price.toString() : '',
    memberPrice: event.memberPrice ? event.memberPrice.toString() : '',
    priceLabel: event.priceLabel || '',
    price2: event.price2 ? event.price2.toString() : '',
    price2Label: event.price2Label || '',
    price3: event.price3 ? event.price3.toString() : '',
    price3Label: event.price3Label || '',
    price4: event.price4 ? event.price4.toString() : '',
    price4Label: event.price4Label || '',
    price5: event.price5 ? event.price5.toString() : '',
    price5Label: event.price5Label || '',
    menuText: event.menuText || '',
    menuHeading: event.menuHeading || '',
    category: event.category || event.type || 'music',
    imageUrl: event.imageUrl || '',
    thumbnailUrl: event.thumbnailUrl || '',
    images: event.images || [],
    mainImageIndex: event.mainImageIndex || 0,
    status: event.status || 'upcoming',
    location: event.location || 'Courtyard'
  });

  setEditingEvent(event);
  setEventImageFiles([]);
  setEventThumbnailFile(null);
};


  const cancelEdit = () => {
    setEditingEvent(null);
    setEventForm({
      title: '',
      subtitle: '',
      richDescription: '',
      date: '',
      endDate: '',
      price: '',
      memberPrice: '',
      priceLabel: '',
      price2: '',
      price2Label: '',
      menuText: '',
      menuHeading: '',
      category: 'music',
      imageUrl: '',
      images: [],
      mainImageIndex: 0,
      status: 'upcoming',
      location: 'Courtyard'
    });

    setEventImageFiles([]);
    setEventThumbnailFile(null);
  };

  const duplicateEvent = (event) => {
    // Use editEvent to populate the form, then clear editingEvent so it creates a new one
    editEvent(event);
    // After editEvent sets the form, clear editingEvent so submit creates a new event
    setTimeout(() => {
      setEditingEvent(null);
      // Clear the date so admin picks a new one
      setEventForm(prev => ({ ...prev, date: '' }));
      // Scroll to the form so it's obvious something happened
      document.querySelector('.admin-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  };

  const createRecurringEvents = async () => {
    if (!editingEvent && !eventForm.date) {
      alert('Please set a date for the first event before creating recurring events.');
      return;
    }

    const { frequency, startDate, endDate } = recurringConfig;
    if (!startDate || !endDate) {
      alert('Please set both a start date and an end date.');
      return;
    }

    // Use the event's time of day
    const sourceDate = new Date(eventForm.date);
    const sourceHours = isNaN(sourceDate.getTime()) ? 19 : sourceDate.getHours();
    const sourceMinutes = isNaN(sourceDate.getTime()) ? 0 : sourceDate.getMinutes();

    const start = new Date(startDate);
    start.setHours(sourceHours, sourceMinutes, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      alert('Invalid date(s).');
      return;
    }

    // Calculate interval in days
    const intervalDays = frequency === 'weekly' ? 7 : frequency === 'fortnightly' ? 14 : 7;

    // Build list of dates from start to end
    const dates = [];
    let nextDate = new Date(start);
    while (nextDate <= end) {
      dates.push(new Date(nextDate));
      nextDate.setDate(nextDate.getDate() + intervalDays);
    }

    if (dates.length === 0) {
      alert('No recurring dates fall between the start and end dates.');
      return;
    }

    if (!window.confirm(`This will create ${dates.length} new event(s) (${frequency}, from ${start.toLocaleDateString('en-GB')} until ${end.toLocaleDateString('en-GB')}). Continue?`)) {
      return;
    }

    setRecurringConfig(prev => ({ ...prev, creatingRecurring: true }));

    try {
      // Build the event data template from the current form/editing event.
      // Convert any embedded base64 images once so all N copies share the same
      // Storage URLs and stay under Firestore's 1 MB per-document limit.
      const recurringRichHtml = await uploadEmbeddedImages(eventForm.richDescription);
      const recurringRichHtmlSv = await uploadEmbeddedImages(eventForm.richDescription_sv);

      const sourceEvent = editingEvent || {};
      const templateData = {
        title: eventForm.title || '',
        subtitle: eventForm.subtitle || '',
        richDescription: recurringRichHtml || '',
        richDescriptionRaw: recurringRichHtml || '',
        title_sv: eventForm.title_sv || '',
        subtitle_sv: eventForm.subtitle_sv || '',
        richDescription_sv: recurringRichHtmlSv || '',
        category: eventForm.category || 'music',
        type: eventForm.category || 'music',
        status: eventForm.status || 'upcoming',
        imageUrl: sourceEvent.imageUrl || eventForm.imageUrl || '',
        images: sourceEvent.images || eventForm.images || [],
        mainImageIndex: eventForm.mainImageIndex || 0,
        thumbnailUrl: sourceEvent.thumbnailUrl || eventForm.thumbnailUrl || '',
        price: eventForm.price ? parseFloat(eventForm.price) : 0,
        memberPrice: eventForm.memberPrice ? parseFloat(eventForm.memberPrice) : 0,
        priceLabel: eventForm.priceLabel || '',
        price2: eventForm.price2 ? parseFloat(eventForm.price2) : 0,
        price2Label: eventForm.price2Label || '',
        price3: eventForm.price3 ? parseFloat(eventForm.price3) : 0,
        price3Label: eventForm.price3Label || '',
        price4: eventForm.price4 ? parseFloat(eventForm.price4) : 0,
        price4Label: eventForm.price4Label || '',
        price5: eventForm.price5 ? parseFloat(eventForm.price5) : 0,
        price5Label: eventForm.price5Label || '',
        priceLabel_sv: eventForm.priceLabel_sv || '',
        price2Label_sv: eventForm.price2Label_sv || '',
        price3Label_sv: eventForm.price3Label_sv || '',
        price4Label_sv: eventForm.price4Label_sv || '',
        price5Label_sv: eventForm.price5Label_sv || '',
        menuText: eventForm.menuText || '',
        menuHeading: eventForm.menuHeading || '',
        menuText_sv: eventForm.menuText_sv || '',
        menuHeading_sv: eventForm.menuHeading_sv || '',
        location: eventForm.location || 'Courtyard',
        isEvent: true,
        createdBy: currentUser.uid,
      };

      // Generate a shared group ID for all events in this series
      const recurringGroupId = editingEvent?.recurringGroupId || `REC-${Date.now()}`;

      // Tag the source event with the group ID if it doesn't have one
      if (editingEvent && !editingEvent.recurringGroupId) {
        await updateDoc(doc(db, 'events', editingEvent.id), { recurringGroupId });
      }

      for (const d of dates) {
        await addDoc(collection(db, 'events'), {
          ...templateData,
          date: d,
          recurringGroupId,
          eventId: `EVT-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          createdAt: new Date(),
        });
      }

      alert(`${dates.length} recurring event(s) created successfully!`);
      setShowRecurring(false);
      setRecurringConfig({ frequency: 'weekly', startDate: '', endDate: '', creatingRecurring: false });
      loadData();
    } catch (error) {
      console.error('Error creating recurring events:', error);
      alert('Error creating recurring events: ' + error.message);
    } finally {
      setRecurringConfig(prev => ({ ...prev, creatingRecurring: false }));
    }
  };

  const deleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteDoc(doc(db, 'events', eventId));
        loadData();
        alert('Event deleted successfully!');
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Error deleting event');
      }
    }
  };

  // Helper function to format date for display
  const formatEventDate = (date) => {
    if (!date) return 'Invalid Date';
    
    try {
      const eventDate = date.toDate ? date.toDate() : new Date(date);
      if (isNaN(eventDate.getTime())) return 'Invalid Date';
      
      return eventDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const deleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteDoc(doc(db, 'products', productId));
        loadData();
        alert('Product deleted successfully!');
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error deleting product');
      }
    }
  };

  // Extract YouTube video ID from URL
  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleYouTubeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const videoId = extractYouTubeId(youtubeForm.url);
      if (!videoId) {
        alert('Please enter a valid YouTube URL');
        setLoading(false);
        return;
      }

      const youtubeData = {
        url: youtubeForm.url,
        videoId: videoId,
        title: youtubeForm.title,
        description: youtubeForm.description,
        thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
        createdAt: new Date(),
        createdBy: currentUser.uid,
        isActive: true
      };
      
      await addDoc(collection(db, 'youtubeVideos'), youtubeData);
      setYoutubeForm({
        url: '',
        title: '',
        description: ''
      });
      loadData();
      alert('YouTube video added successfully!');
    } catch (error) {
      console.error('Error adding YouTube video:', error);
      alert('Error adding YouTube video');
    }
    setLoading(false);
  };

  const deleteYouTubeVideo = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this YouTube video?')) {
      try {
        await deleteDoc(doc(db, 'youtubeVideos', videoId));
        loadData();
        alert('YouTube video deleted successfully!');
      } catch (error) {
        console.error('Error deleting YouTube video:', error);
        alert('Error deleting YouTube video');
      }
    }
  };

  const handleCarouselImageSubmit = async (e) => {
    e.preventDefault();
    
    // Check if we already have 10 images
    if (carouselImages.length >= 10) {
      alert('Maximum of 10 carousel images allowed. Please delete some images first.');
      return;
    }

    if (!carouselImageFile) {
      alert('Please select an image file to upload.');
      return;
    }

    setLoading(true);
    try {
      const imageUrl = await uploadCarouselImage();
      if (!imageUrl) {
        setLoading(false);
        return;
      }

      const carouselData = {
        imageUrl: imageUrl,
        createdAt: new Date(),
        createdBy: currentUser.uid
      };
      
      await addDoc(collection(db, 'carouselImages'), carouselData);
      setCarouselImageFile(null);
      loadData();
      alert('Carousel image added successfully!');
    } catch (error) {
      console.error('Error adding carousel image:', error);
      alert('Error adding carousel image');
    }
    setLoading(false);
  };

  const deleteCarouselImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this carousel image?')) {
      try {
        await deleteDoc(doc(db, 'carouselImages', imageId));
        loadData();
        alert('Carousel image deleted successfully!');
      } catch (error) {
        console.error('Error deleting carousel image:', error);
        alert('Error deleting carousel image');
      }
    }
  };

  const handleAdminUserSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Try to use the Cloud Function if available
      const createAdminUser = httpsCallable(functions, 'createAdminUser');
      
      try {
        const result = await createAdminUser({ 
          email: adminForm.email, 
          password: adminForm.password 
        });
        
        if (result.data.success) {
          alert(`Admin user created successfully for ${adminForm.email}`);
          setAdminForm({ email: '', password: '' });
          loadData();
        } else {
          throw new Error(result.data.error || 'Failed to create admin user');
        }
      } catch (functionError) {
        // If Cloud Function fails, try direct approach
        console.log('Cloud Function not available, using direct approach');
        
        // Create the user account
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          adminForm.email, 
          adminForm.password
        );
        
        // Add admin role to Firestore
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email: adminForm.email,
          role: 'admin',
          isMember: true,
          createdAt: new Date(),
          createdBy: currentUser.uid
        });
        
        alert(`Admin user created successfully for ${adminForm.email}`);
        setAdminForm({ email: '', password: '' });
        loadData();
      }
    } catch (error) {
      console.error('Error creating admin user:', error);
      if (error.code === 'auth/email-already-in-use') {
        alert('This email is already registered. The user needs to sign in and an existing admin can grant them admin privileges.');
      } else if (error.code === 'auth/weak-password') {
        alert('Password should be at least 6 characters.');
      } else if (error.code === 'auth/invalid-email') {
        alert('Please enter a valid email address.');
      } else {
        alert(`Error creating admin user: ${error.message}`);
      }
    }
    
    setLoading(false);
  };

  const removeAdminRole = async (userId, userEmail) => {
    if (userId === currentUser.uid) {
      alert("You cannot remove your own admin privileges!");
      return;
    }
    
    if (window.confirm(`Are you sure you want to remove admin privileges from ${userEmail}?`)) {
      try {
        await updateDoc(doc(db, 'users', userId), {
          role: 'user',
          isMember: false
        });
        alert(`Admin privileges removed from ${userEmail}`);
        loadData();
      } catch (error) {
        console.error('Error removing admin role:', error);
        alert('Error removing admin privileges');
      }
    }
  };

  // Contact messages actions
  const markContactStatus = async (messageId, status) => {
    try {
      await updateDoc(doc(db, 'contactMessages', messageId), { status });
      loadData();
    } catch (error) {
      console.error('Error updating message status:', error);
      alert('Error updating message status');
    }
  };

  const deleteContactMessage = async (messageId) => {
    if (window.confirm('Delete this message?')) {
      try {
        await deleteDoc(doc(db, 'contactMessages', messageId));
        loadData();
      } catch (error) {
        console.error('Error deleting message:', error);
        alert('Error deleting message');
      }
    }
  };

  const renderContactMessages = () => {
    const filtered = contactMessages.filter(m => contactFilter === 'all' || (m.status || 'new') === contactFilter);
    if (filtered.length === 0) {
      return <p>No messages yet.</p>;
    }
    return filtered.map((m) => (
      <div key={m.id} className="admin-item">
        <div className="item-info">
          <h4>{m.name || 'Anonymous'}</h4>
          <p><strong>Email:</strong> {m.email ? <a href={'mailto:' + m.email}>{m.email}</a> : '—'}</p>
          <p><strong>Phone:</strong> {m.phone || '—'}</p>
          <p><strong>Message:</strong> {m.message || ''}</p>
          <p><strong>Created:</strong> {m.createdAt && m.createdAt.toDate ? m.createdAt.toDate().toLocaleString() : 'Unknown'}</p>
          <p><strong>Status:</strong> {(m.status || 'new').toUpperCase()}</p>
        </div>
        <div className="item-actions">
          {(m.status || 'new') !== 'resolved' ? (
            <button onClick={() => markContactStatus(m.id, 'resolved')} className="edit-btn">
              RESOLVE
            </button>
          ) : (
            <button onClick={() => markContactStatus(m.id, 'new')} className="edit-btn">
              MARK NEW
            </button>
          )}
          <button onClick={() => deleteContactMessage(m.id)} className="delete-btn">
            DELETE
          </button>
        </div>
      </div>
    ));
  };

  if (!currentUser || !isAdmin()) {
    return <div>Access denied. Admin privileges required.</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
          <h1 style={{ margin: 0 }}>ADMIN DASHBOARD</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '13px', color: '#555' }}>
              Signed in as <strong>{currentUser.email}</strong>
            </span>
            <button
              onClick={handleSignOut}
              style={{
                padding: '8px 16px',
                border: '2px solid #191919',
                background: '#fff',
                color: '#191919',
                fontFamily: 'inherit',
                fontSize: '12px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                cursor: 'pointer'
              }}
            >
              Sign out
            </button>
          </div>
        </div>
        <div className="admin-tabs">
          <button 
            className={activeTab === 'events' ? 'active' : ''}
            onClick={() => setActiveTab('events')}
          >
            EVENTS
          </button>
          <button 
            className={activeTab === 'products' ? 'active' : ''}
            onClick={() => setActiveTab('products')}
          >
            SHOP
          </button>
          <button 
            className={activeTab === 'orders' ? 'active' : ''}
            onClick={() => setActiveTab('orders')}
          >
            ORDERS
          </button>
          <button 
            className={activeTab === 'contact' ? 'active' : ''}
            onClick={() => setActiveTab('contact')}
          >
            CONTACT
          </button>
          <button 
            className={activeTab === 'youtube' ? 'active' : ''}
            onClick={() => setActiveTab('youtube')}
          >
            YOUTUBE
          </button>
          <button 
            className={activeTab === 'carousel' ? 'active' : ''}
            onClick={() => setActiveTab('carousel')}
          >
            CAROUSEL
          </button>
          <button 
            className={activeTab === 'calendar' ? 'active' : ''}
            onClick={() => setActiveTab('calendar')}
          >
            CALENDAR
          </button>
          <button 
            className={activeTab === 'admins' ? 'active' : ''}
            onClick={() => setActiveTab('admins')}
          >
            ADMINS
          </button>
          <button
            className={activeTab === 'invoicing' ? 'active' : ''}
            onClick={() => setActiveTab('invoicing')}
          >
            INVOICING
          </button>
          <Link to="/ticket-validator">
            <button>
              VALIDATOR
            </button>
          </Link>
        </div>
      </div>

      {activeTab === 'events' && (
        <div className="admin-section">
          <h2>MANAGE EVENTS</h2>
          
          <div className="admin-form">
            <h3>{editingEvent ? 'EDIT EVENT' : 'ADD NEW EVENT'}</h3>
            {editingEvent && (<>
              <div className="edit-mode-notice">
                <p>Editing: {editingEvent.title}</p>
                <button type="button" onClick={cancelEdit} className="cancel-edit-btn">
                  CANCEL EDIT
                </button>

                <button
                  type="button"
                  onClick={() => setShowEventPreview(true)}
                  style={{ background: '#2c3e2f', color: '#fff' }}
                >
                  PREVIEW
                </button>

                <button
                  type="button"
                  onClick={() => duplicateEvent(editingEvent)}
                  className="duplicate-btn"
                >
                  DUPLICATE
                </button>

                <button
                  type="button"
                  onClick={() => setShowRecurring(true)}
                  className="recurring-btn"
                >
                  MAKE RECURRING
                </button>

                {/* This button is *inside the form* via form="eventForm" */}
                <button
                  type="submit"
                  form="eventForm"
                  disabled={loading || uploadingEventImages || uploadingEventThumbnail}
                >
                  {loading ? 'UPDATING…' : 'UPDATE EVENT'}
                </button>
              </div>

              {/* Recurring Event Modal */}
              {showRecurring && (
                <div style={{
                  margin: '1rem 0', padding: '1rem', background: '#f4f0ff',
                  border: '1px solid #6c5ce7', borderRadius: '6px'
                }}>
                  <h4 style={{ margin: '0 0 0.75rem', color: '#6c5ce7' }}>Create Recurring Events</h4>
                  <p style={{ fontSize: '0.85rem', color: '#555', marginBottom: '0.75rem' }}>
                    This will create copies of "{eventForm.title}" at the same time ({eventForm.date ? new Date(eventForm.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}), on the same day of the week.
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '0.9rem' }}>
                      Repeat:
                      <select
                        value={recurringConfig.frequency}
                        onChange={(e) => setRecurringConfig({ ...recurringConfig, frequency: e.target.value })}
                        style={{ marginLeft: '0.5rem', padding: '0.4rem' }}
                      >
                        <option value="weekly">Every week</option>
                        <option value="fortnightly">Every 2 weeks</option>
                      </select>
                    </label>
                    <label style={{ fontSize: '0.9rem' }}>
                      From:
                      <input
                        type="date"
                        value={recurringConfig.startDate}
                        onChange={(e) => setRecurringConfig({ ...recurringConfig, startDate: e.target.value })}
                        style={{ marginLeft: '0.5rem', padding: '0.4rem' }}
                      />
                    </label>
                    <label style={{ fontSize: '0.9rem' }}>
                      Until:
                      <input
                        type="date"
                        value={recurringConfig.endDate}
                        onChange={(e) => setRecurringConfig({ ...recurringConfig, endDate: e.target.value })}
                        style={{ marginLeft: '0.5rem', padding: '0.4rem' }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={createRecurringEvents}
                      disabled={recurringConfig.creatingRecurring}
                      style={{ background: '#6c5ce7', color: '#fff', padding: '0.5rem 1rem' }}
                    >
                      {recurringConfig.creatingRecurring ? 'CREATING…' : 'CREATE SERIES'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRecurring(false)}
                      style={{ background: '#ccc', color: '#333', padding: '0.5rem 1rem' }}
                    >
                      CANCEL
                    </button>
                  </div>
                </div>
              )}
            </>
            )}
                
            <form id="eventForm" onSubmit={handleEventSubmit}>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Event Title"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                  required
                />
                <select
                  value={eventForm.category}
                  onChange={(e) => setEventForm({...eventForm, category: e.target.value})}
                >
                  <option value="music">Music Events</option>
                  <option value="food">Food Events</option>
                  <option value="workshop">Workshop</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <input
                type="text"
                placeholder="Event Subtitle (e.g., '+ Candice Gordon')"
                value={eventForm.subtitle}
                onChange={(e) => setEventForm({...eventForm, subtitle: e.target.value})}
                required
              />
              
              <div className="rich-editor-section">
                <label>Rich Description (Optional - for detailed event page):</label>
<Suspense fallback={<div>Loading editor…</div>}>
  <RichTextEditorClassic
    value={eventForm.richDescription}
    onChange={(html) => setEventForm((f) => ({ ...f, richDescription: html }))}
  />
</Suspense>

              </div>
              
              <div className="form-row">
                <input
                  type="datetime-local"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                  step="1800"
                  required
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#555' }}>
                  Ends (multi-day only):
                  {/* No step attr: step is counted from `min`, so combining it
                      with a minute-precision start time rejects round times */}
                  <input
                    type="datetime-local"
                    value={eventForm.endDate}
                    onChange={(e) => setEventForm({...eventForm, endDate: e.target.value})}
                    min={eventForm.date || undefined}
                  />
                </label>
                <select
                  value={eventForm.location}
                  onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                  required
                >
                  <option value="Courtyard">Courtyard</option>
                  <option value="Hayloft">Hayloft</option>
                  <option value="Recording Studio">Recording Studio</option>
                  <option value="Kitchen">Kitchen</option>
                  <option value="Garden">Garden</option>
                  <option value="Main Hall">Main Hall</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <label style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '0.25rem' }}>
                Ticket options (leave price empty to hide):
              </label>
              <div style={{
                display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem'
              }}>
                {[
                  { priceKey: 'price', labelKey: 'priceLabel', num: 1 },
                  { priceKey: 'price2', labelKey: 'price2Label', num: 2 },
                  { priceKey: 'price3', labelKey: 'price3Label', num: 3 },
                  { priceKey: 'price4', labelKey: 'price4Label', num: 4 },
                  { priceKey: 'price5', labelKey: 'price5Label', num: 5 },
                ].map(({ priceKey, labelKey, num }) => (
                  <React.Fragment key={num}>
                    <input
                      type="number"
                      placeholder={`Price ${num} (SEK)`}
                      value={eventForm[priceKey]}
                      onChange={(e) => setEventForm({...eventForm, [priceKey]: e.target.value})}
                      style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <input
                      type="text"
                      placeholder={`Label ${num} (e.g. Concert + Food)`}
                      value={eventForm[labelKey]}
                      onChange={(e) => setEventForm({...eventForm, [labelKey]: e.target.value})}
                      style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </React.Fragment>
                ))}
              </div>
              <label style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem', display: 'block' }}>
                Food Menu (optional - supports formatting):
              </label>
              <input
                type="text"
                placeholder="Heading (default: Menu)"
                value={eventForm.menuHeading}
                onChange={(e) => setEventForm({...eventForm, menuHeading: e.target.value})}
                style={{ marginBottom: '0.5rem' }}
              />
              <Suspense fallback={<div>Loading editor…</div>}>
                <RichTextEditorClassic
                  value={eventForm.menuText}
                  onChange={(html) => setEventForm((f) => ({ ...f, menuText: html }))}
                />
              </Suspense>

              {/* Swedish Translation Section */}
              <div style={{ marginTop: '1.5rem', borderTop: '2px solid #ccc', paddingTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setShowSwedish(!showSwedish)}
                  style={{
                    background: '#2c3e2f',
                    color: '#fff',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontFamily: 'Jost, sans-serif',
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  {showSwedish ? '▼' : '▶'} Swedish Translation (optional)
                </button>
                {showSwedish && (
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f0f2ec', borderRadius: '4px' }}>
                    <input
                      type="text"
                      placeholder="Titel (svenska)"
                      value={eventForm.title_sv}
                      onChange={(e) => setEventForm({...eventForm, title_sv: e.target.value})}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    <input
                      type="text"
                      placeholder="Underrubrik (svenska)"
                      value={eventForm.subtitle_sv}
                      onChange={(e) => setEventForm({...eventForm, subtitle_sv: e.target.value})}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    <label style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '0.25rem' }}>
                      Beskrivning (svenska):
                    </label>
                    <Suspense fallback={<div>Loading editor…</div>}>
                      <RichTextEditorClassic
                        value={eventForm.richDescription_sv}
                        onChange={(html) => setEventForm((f) => ({ ...f, richDescription_sv: html }))}
                      />
                    </Suspense>
                    <input
                      type="text"
                      placeholder="Menyrubriker (svenska, default: Meny)"
                      value={eventForm.menuHeading_sv}
                      onChange={(e) => setEventForm({...eventForm, menuHeading_sv: e.target.value})}
                      style={{ marginTop: '0.75rem', marginBottom: '0.5rem' }}
                    />
                    <label style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '0.25rem' }}>
                      Menytext (svenska):
                    </label>
                    <Suspense fallback={<div>Loading editor…</div>}>
                      <RichTextEditorClassic
                        value={eventForm.menuText_sv}
                        onChange={(html) => setEventForm((f) => ({ ...f, menuText_sv: html }))}
                      />
                    </Suspense>
                    <input
                      type="text"
                      placeholder="Prisetikett 1 (svenska, t.ex. Konsert + Mat)"
                      value={eventForm.priceLabel_sv}
                      onChange={(e) => setEventForm({...eventForm, priceLabel_sv: e.target.value})}
                      style={{ marginTop: '0.75rem', marginBottom: '0.5rem' }}
                    />
                    <input
                      type="text"
                      placeholder="Prisetikett 2 (svenska, t.ex. Endast konsert)"
                      value={eventForm.price2Label_sv}
                      onChange={(e) => setEventForm({...eventForm, price2Label_sv: e.target.value})}
                      style={{ marginBottom: '0.5rem' }}
                    />
                  </div>
                )}
              </div>

              <div className="image-upload-section">
                <h4>Event Images</h4>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                  Upload multiple images for the event gallery. The thumbnail (below) will be used for event cards.
                </p>
                
                {/* Display current images if editing */}
                {eventForm.images && eventForm.images.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <label>Current Images (select main image with checkbox):</label>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                      gap: '1rem',
                      marginTop: '1rem' 
                    }}>
                      {eventForm.images.map((image, index) => (
                        <div key={index} style={{ 
                          position: 'relative',
                          border: index === (eventForm.mainImageIndex || 0) ? '3px solid #191919' : '1px solid #ccc',
                          padding: '0.5rem',
                          borderRadius: '4px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            marginBottom: '0.5rem'
                          }}>
                            <input
                              type="checkbox"
                              checked={index === (eventForm.mainImageIndex || 0)}
                              onChange={() => setEventForm({...eventForm, mainImageIndex: index})}
                              style={{ cursor: 'pointer' }}
                            />
                            <label style={{ fontSize: '0.8rem', fontWeight: index === (eventForm.mainImageIndex || 0) ? 'bold' : 'normal' }}>
                              {index === (eventForm.mainImageIndex || 0) ? 'MAIN' : 'Set as main'}
                            </label>
                          </div>
                          <img 
                            src={image} 
                            alt={`Event ${index + 1}`} 
                            style={{ 
                              width: '100%', 
                              height: '100px', 
                              objectFit: 'cover',
                              marginBottom: '0.5rem'
                            }} 
                          />
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button 
                              type="button"
                              onClick={() => removeEventImage(index)}
                              style={{ 
                                fontSize: '0.8rem',
                                padding: '2px 6px',
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '2px',
                                cursor: 'pointer'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
              

                <div className="file-upload" style={{ marginTop: '1rem' }}>
  <label>Upload Images:</label>
  <input
    type="file"
    accept="image/*"
    multiple
    onChange={(e) => {
      const newFiles = Array.from(e.target.files);
      // Add new files to existing selection instead of replacing
      setEventImageFiles(prevFiles => [...prevFiles, ...newFiles]);
    }}
  />
  {eventImageFiles.length > 0 && (
    <div style={{ marginTop: '0.5rem' }}>
      <span>Selected: {eventImageFiles.length} image(s)</span>
      <button
        type="button"
        onClick={async () => {
          const uploadedUrls = await uploadEventImages();
          if (uploadedUrls.length > 0) {
            setEventForm(prev => ({
              ...prev,
              images: [...(prev.images || []), ...uploadedUrls]
            }));
            setEventImageFiles([]);
          }
        }}
        disabled={uploadingEventImages}
        style={{
          marginLeft: '1rem',
          padding: '4px 12px',
          background: '#191919',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: uploadingEventImages ? 'not-allowed' : 'pointer'
        }}
      >
        {uploadingEventImages ? 'Uploading...' : 'Upload Now'}
      </button>
    </div>
  )}
</div>

                <div className="file-upload" style={{ marginTop: '1rem' }}>
                  <label>Thumbnail (square) for cards:</label>
                  {(eventForm.thumbnailUrl || eventThumbnailFile) && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <label>{eventThumbnailFile ? 'New Thumbnail:' : 'Current Thumbnail:'}</label>
                      <div style={{ width: 120, height: 120, border: '1px solid #ccc', overflow: 'hidden', borderRadius: 4 }}>
                        <img 
                          src={eventThumbnailFile ? URL.createObjectURL(eventThumbnailFile) : eventForm.thumbnailUrl} 
                          alt="Thumbnail preview" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        />
                      </div>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setEventThumbnailFile(file);
                      // If editing and there's a new file, update the form to trigger re-render
                      if (file && editingEvent) {
                        setEventForm({...eventForm});
                      }
                    }}
                  />
                  {eventThumbnailFile && <span>Selected: {eventThumbnailFile.name} (square required)</span>}
                  {uploadingEventThumbnail && <span>Uploading thumbnail…</span>}
                </div>
              </div>
              
              <div style={{ display: editingEvent ? 'none' : 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="cancel-edit-btn"
                >
                  CLEAR FORM
                </button>
                <button
                  type="button"
                  onClick={() => setShowEventPreview(true)}
                  style={{ background: '#2c3e2f', color: '#fff' }}
                >
                  PREVIEW
                </button>
                <button
                  type="submit"
                  disabled={loading || uploadingEventImages || uploadingEventThumbnail}
                >
                  {loading ? 'CREATING...' : 'CREATE EVENT'}
                </button>
              </div>
            </form>
          </div>

          {(() => {
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            const getEventDate = (ev) => {
              if (!ev.date) return null;
              return ev.date.toDate ? ev.date.toDate() : new Date(ev.date);
            };
            const upcoming = events.filter(ev => {
              const d = getEventDate(ev);
              return d && d >= startOfToday;
            }).sort((a, b) => getEventDate(a) - getEventDate(b));
            const past = events.filter(ev => {
              const d = getEventDate(ev);
              return !d || d < startOfToday;
            }).sort((a, b) => getEventDate(b) - getEventDate(a));

            const renderEventRow = (event) => (
              <div key={event.id} className="admin-item">
                <div className="item-info">
                  <h4>{event.title}</h4>
                  {event.subtitle && <p><em>{event.subtitle}</em></p>}
                  <p>Date: {formatEventDate(event.date)}</p>
                  <p>{event.price > 0 ? `Price: ${event.price} SEK` : 'No ticket price'}</p>
                </div>
                <div className="item-actions">
                  <button onClick={() => editEvent(event)} className="edit-btn">
                    EDIT
                  </button>
                  <button onClick={() => duplicateEvent(event)} className="duplicate-btn">
                    DUPLICATE
                  </button>
                  <button onClick={() => deleteEvent(event.id)} className="delete-btn">
                    DELETE
                  </button>
                </div>
              </div>
            );

            return (
              <>
                <div className="admin-list">
                  <h3>UPCOMING EVENTS ({upcoming.length})</h3>
                  {upcoming.length === 0 && <p style={{ color: '#888' }}>No upcoming events</p>}
                  {upcoming.map(renderEventRow)}
                </div>
                <div className="admin-list" style={{ marginTop: '2rem' }}>
                  <h3 style={{ color: '#888' }}>PAST EVENTS ({past.length})</h3>
                  {past.map(renderEventRow)}
                </div>
              </>
            );
          })()}

          {/* Event Preview Modal */}
          {showEventPreview && (() => {
            // Build preview image list: use saved images, local file previews, or thumbnail
            const previewImages = (() => {
              // Newly selected main image files (not yet uploaded)
              if (eventImageFiles && eventImageFiles.length > 0) {
                return Array.from(eventImageFiles).map(f => URL.createObjectURL(f));
              }
              // Saved main images from an existing event
              if (editingEvent?.images?.length) return editingEvent.images;
              if (editingEvent?.imageUrl) return [editingEvent.imageUrl];
              if (eventForm.images?.length) return eventForm.images;
              // Fall back to thumbnail
              if (eventThumbnailFile) return [URL.createObjectURL(eventThumbnailFile)];
              if (editingEvent?.thumbnailUrl) return [editingEvent.thumbnailUrl];
              if (eventForm.thumbnailUrl) return [eventForm.thumbnailUrl];
              return [];
            })();

            return (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.7)', zIndex: 9999,
              overflow: 'auto', padding: '2rem'
            }} onClick={() => setShowEventPreview(false)}>
              <div style={{
                maxWidth: '900px', margin: '0 auto', background: '#fff',
                borderRadius: '8px', overflow: 'hidden', position: 'relative'
              }} onClick={e => e.stopPropagation()}>
                <div style={{
                  position: 'sticky', top: 0, background: '#2c3e2f', color: '#fff',
                  padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1
                }}>
                  <span style={{ fontWeight: 700 }}>PREVIEW</span>
                  <button onClick={() => setShowEventPreview(false)} style={{
                    background: 'transparent', border: '1px solid #fff', color: '#fff',
                    padding: '4px 12px', cursor: 'pointer', borderRadius: '4px'
                  }}>CLOSE</button>
                </div>

                {/* Preview content — uses event-detail-page classes for styling but with layout overrides */}
                <div style={{ minHeight: 'auto', padding: '1.5rem', overflow: 'hidden', fontFamily: "'Jost', sans-serif" }}>
                  <div style={{
                    display: 'flex', flexDirection: 'row', gap: '2rem',
                    maxWidth: '100%', alignItems: 'flex-start'
                  }}>
                    <div style={{ flex: '1 1 50%', minWidth: 0 }}>
                      {previewImages.length > 0 && (
                        <div className="event-main-image">
                          <img src={previewImages[0]} alt="Preview" style={{ width: '100%', borderRadius: '4px' }} />
                        </div>
                      )}
                      {previewImages.length > 1 && (
                        <div className="event-thumbnail-gallery">
                          {previewImages.map((img, i) => (
                            <div key={i} className={`thumbnail ${i === 0 ? 'active' : ''}`}>
                              <img src={img} alt={`Thumb ${i + 1}`} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ flex: '1 1 45%', minWidth: 0 }}>
                      <div className="event-info-panel">
                          <h1 className="event-title">{eventForm.title || 'Untitled Event'}</h1>

                          {eventForm.subtitle && (
                            <p className="event-subtitle">{eventForm.subtitle}</p>
                          )}

                          {eventForm.date && (
                            <div className="event-datetime">
                              <div className="event-date">
                                {(() => {
                                  const d = new Date(eventForm.date);
                                  return d.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                                })()}
                              </div>
                              <div className="event-time">
                                Doors: {(() => {
                                  const d = new Date(eventForm.date);
                                  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                                })()}
                              </div>
                              {eventForm.location && (
                                <div className="event-location">Location: {eventForm.location}</div>
                              )}
                            </div>
                          )}

                          {(() => {
                            const tiers = [
                              { price: eventForm.price, label: eventForm.priceLabel },
                              { price: eventForm.price2, label: eventForm.price2Label },
                              { price: eventForm.price3, label: eventForm.price3Label },
                              { price: eventForm.price4, label: eventForm.price4Label },
                              { price: eventForm.price5, label: eventForm.price5Label },
                            ].filter(t => t.price && parseFloat(t.price) > 0);

                            if (tiers.length === 0) return null;
                            if (tiers.length === 1) {
                              return (
                                <div className="event-tickets">
                                  <span className="ticket-button highlighted">Tickets: {tiers[0].price} kr</span>
                                </div>
                              );
                            }
                            return (
                              <div className="event-ticket-tiers">
                                {tiers.map((tier, i) => (
                                  <div className="ticket-tier-row" key={i}>
                                    <span className="ticket-tier-label">{tier.label || `Option ${i + 1}`}</span>
                                    <span className="ticket-button highlighted">{tier.price} kr</span>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}

                          {eventForm.menuText && (
                            <div className="event-menu-box">
                              <h3 className="event-menu-heading">{eventForm.menuHeading || 'Menu'}</h3>
                              <div
                                className="event-menu-content"
                                dangerouslySetInnerHTML={{ __html: analyzeAndSanitize(eventForm.menuText).html }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                  {eventForm.richDescription && (
                    <div style={{ padding: '1.5rem', borderTop: '1px solid #e0e2dc' }}>
                      <div
                        className="rte-display"
                        dangerouslySetInnerHTML={{ __html: analyzeAndSanitize(eventForm.richDescription).html }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })()}
        </div>

      )}

      {activeTab === 'products' && (
        <div className="admin-section">
          <h2>MANAGE SHOP</h2>
          
          <div className="admin-form">
            <h3>{editingProduct ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}</h3>
            {editingProduct && (
              <div className="edit-mode-notice">
                <p>Editing: {editingProduct.name}</p>
                <button type="button" onClick={cancelEditProduct} className="cancel-edit-btn">
                  CANCEL EDIT
                </button>
                
                <button
                  type="submit"
                  form="productForm"
                  disabled={loading || uploadingProductImage || uploadingProductImages || uploadingProductThumbnail}
                >
                  {loading ? 'UPDATING…' : 'UPDATE PRODUCT'}
                </button>
              </div>
            )}
            <form id="productForm" onSubmit={handleProductSubmit}>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Product Name"
                  value={productForm.name}
                  onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                  required
                />
                <select
                  value={productForm.category}
                  onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                >
                  <option value="tickets">Tickets</option>
                  <option value="merchandise">Merchandise</option>
                  <option value="crafts">Crafts</option>
                  <option value="recordings">Recordings</option>
                </select>
                <select
                  value={productForm.type}
                  onChange={(e) => setProductForm({...productForm, type: e.target.value})}
                >
                  <option value="physical">Physical</option>
                  <option value="digital">Digital</option>
                </select>
              </div>
              
              <textarea
                placeholder="Product Description (brief)"
                value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                required
              />
              
              <div className="rich-editor-section">
                <label>Rich Description (Optional - for detailed product page):</label>
                <Suspense fallback={<div>Loading editor…</div>}>
                  <RichTextEditorClassic
                    value={productForm.richDescription}
                    onChange={(html) => setProductForm((f) => ({ ...f, richDescription: html }))}
                  />
                </Suspense>
              </div>
              
              <div className="form-row">
                <label className="admin-field">
                  <span>{productForm.downloadEnabled ? 'Physical copy price (SEK)' : 'Price (SEK)'}</span>
                  <input
                    type="number"
                    placeholder="Price (SEK)"
                    value={productForm.price}
                    onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                    required
                  />
                </label>
                <label className="admin-field">
                  <span>Member price (SEK)</span>
                  <input
                    type="number"
                    placeholder="Member Price (SEK)"
                    value={productForm.memberPrice}
                    onChange={(e) => setProductForm({...productForm, memberPrice: e.target.value})}
                    required
                  />
                </label>
                <label className="admin-field">
                  <span>Stock quantity</span>
                  <input
                    type="number"
                    placeholder="Stock Quantity"
                    value={productForm.stock}
                    onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                    required
                  />
                </label>
              </div>
              
              <div className="download-option-section" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                <h4>Digital Download Option</h4>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <input
                    type="checkbox"
                    checked={productForm.downloadEnabled}
                    onChange={(e) => setProductForm({...productForm, downloadEnabled: e.target.checked})}
                  />
                  Also sell as a digital download (shown as a choice on the product page, with its own price)
                </label>
                {productForm.downloadEnabled && (
                  <>
                    <div className="form-row">
                      <label className="admin-field">
                        <span>Physical option name</span>
                        <input
                          type="text"
                          placeholder="e.g. Vinyl LP + Download"
                          value={productForm.physicalLabel}
                          onChange={(e) => setProductForm({...productForm, physicalLabel: e.target.value})}
                        />
                      </label>
                      <label className="admin-field">
                        <span>Download option name</span>
                        <input
                          type="text"
                          placeholder="e.g. 320 / WAV Download"
                          value={productForm.downloadLabel}
                          onChange={(e) => setProductForm({...productForm, downloadLabel: e.target.value})}
                        />
                      </label>
                      <label className="admin-field">
                        <span>Download price (SEK)</span>
                        <input
                          type="number"
                          placeholder="Download Price (SEK)"
                          value={productForm.downloadPrice}
                          onChange={(e) => setProductForm({...productForm, downloadPrice: e.target.value})}
                          min="0"
                          step="0.01"
                          required
                        />
                      </label>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0 1rem' }}>
                      <input
                        type="checkbox"
                        checked={productForm.physicalIncludesDownload}
                        onChange={(e) => setProductForm({...productForm, physicalIncludesDownload: e.target.checked})}
                      />
                      Physical copy also includes the download (buyers of the physical item get the link too)
                    </label>
                    <div className="file-upload">
                      <label>Download file (e.g. a .zip of the album):</label>
                      {productForm.downloadFileName && !productDownloadFile && (
                        <p style={{ fontSize: '0.9rem', color: '#666', margin: '0.25rem 0' }}>
                          Current file: <strong>{productForm.downloadFileName}</strong> (choose a new file to replace it)
                        </p>
                      )}
                      <input
                        type="file"
                        onChange={(e) => setProductDownloadFile(e.target.files[0] || null)}
                      />
                      {downloadUploadProgress !== null && (
                        <p style={{ fontSize: '0.9rem', margin: '0.25rem 0' }}>Uploading download file… {downloadUploadProgress}%</p>
                      )}
                      <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                        The file is kept private. Buyers get a personal download link by email after paying.
                      </p>
                    </div>
                  </>
                )}
              </div>

              <div className="postage-section" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                <h4>Postage Fees (SEK) - Optional</h4>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                  Add postage fees for physical items. Leave blank if no postage applies.
                </p>
                <div className="form-row">
                  <input
                    type="number"
                    placeholder="Sweden Postage (SEK)"
                    value={productForm.postage?.sweden || ''}
                    onChange={(e) => setProductForm({
                      ...productForm, 
                      postage: {...(productForm.postage || {}), sweden: e.target.value}
                    })}
                    min="0"
                    step="0.01"
                  />
                  <input
                    type="number"
                    placeholder="Europe Postage (SEK)"
                    value={productForm.postage?.europe || ''}
                    onChange={(e) => setProductForm({
                      ...productForm, 
                      postage: {...(productForm.postage || {}), europe: e.target.value}
                    })}
                    min="0"
                    step="0.01"
                  />
                  <input
                    type="number"
                    placeholder="Worldwide Postage (SEK)"
                    value={productForm.postage?.worldwide || ''}
                    onChange={(e) => setProductForm({
                      ...productForm, 
                      postage: {...(productForm.postage || {}), worldwide: e.target.value}
                    })}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              
              <div className="image-upload-section">
                <h4>Product Images</h4>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                  Upload multiple images for the product gallery. The thumbnail (below) will be used for product cards.
                </p>
                
                {/* Display current images if editing */}
                {productForm.images && productForm.images.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <label>Current Images:</label>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                      gap: '1rem',
                      marginTop: '1rem' 
                    }}>
                      {productForm.images.map((image, index) => (
                        <div key={index} style={{ 
                          position: 'relative',
                          border: index === 0 ? '3px solid #191919' : '1px solid #ccc',
                          padding: '0.5rem',
                          borderRadius: '4px'
                        }}>
                          <img 
                            src={image} 
                            alt={`Product ${index + 1}`} 
                            style={{ 
                              width: '100%', 
                              height: '100px', 
                              objectFit: 'cover',
                              marginBottom: '0.5rem'
                            }} 
                          />
                          {index === 0 && (
                            <div style={{ 
                              position: 'absolute', 
                              top: '0', 
                              left: '0', 
                              background: '#191919', 
                              color: 'white', 
                              padding: '2px 6px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold'
                            }}>
                              MAIN
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button 
                              type="button"
                              onClick={() => removeProductImage(index)}
                              style={{ 
                                fontSize: '0.8rem',
                                padding: '2px 6px',
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '2px',
                                cursor: 'pointer'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="file-upload" style={{ marginTop: '1rem' }}>
                  <label>Upload Images:</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const newFiles = Array.from(e.target.files);
                      // Add new files to existing selection instead of replacing
                      setProductImageFiles(prevFiles => [...prevFiles, ...newFiles]);
                    }}
                  />
                  {productImageFiles.length > 0 && (
                    <span>Selected: {productImageFiles.length} image(s)</span>
                  )}
                  {uploadingProductImages && <span>Uploading images...</span>}
                </div>
              </div>

              <div className="file-upload" style={{ marginTop: '1rem' }}>
                <label>Thumbnail (square) for cards:</label>
                {(productForm.thumbnailUrl || productThumbnailFile) && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <label>{productThumbnailFile ? 'New Thumbnail:' : 'Current Thumbnail:'}</label>
                    <div style={{ width: 120, height: 120, border: '1px solid #ccc', overflow: 'hidden', borderRadius: 4 }}>
                      <img 
                        src={productThumbnailFile ? URL.createObjectURL(productThumbnailFile) : productForm.thumbnailUrl} 
                        alt="Thumbnail preview" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                    </div>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setProductThumbnailFile(file);
                    // If editing and there's a new file, update the form to trigger re-render
                    if (file && editingProduct) {
                      setProductForm({...productForm});
                    }
                  }}
                />
                {productThumbnailFile && <span>Selected: {productThumbnailFile.name} (square required)</span>}
                {uploadingProductThumbnail && <span>Uploading thumbnail…</span>}
              </div>
              
              {!editingProduct && (
                <button type="submit" disabled={loading || uploadingProductImage || uploadingProductImages || uploadingProductThumbnail}>
                  {loading ? 'CREATING...' : 'CREATE PRODUCT'}
                </button>
              )}
            </form>
          </div>

          <div className="admin-list">
            <h3>EXISTING PRODUCTS</h3>
            {products.map(product => (
              <div key={product.id} className="admin-item">
                <div className="item-info">
                  <h4>{product.name}</h4>
                  <p>{product.description}</p>
                  <p>Price: {product.price} SEK | Member: {product.memberPrice} SEK</p>
                  <p>Stock: {product.stock} | Type: {product.type}</p>
                </div>
                <div className="item-actions">
                  <button onClick={() => editProduct(product)} className="edit-btn">
                    EDIT
                  </button>
                  <button onClick={() => deleteProduct(product.id)} className="delete-btn">
                    DELETE
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="admin-section">
          <h2>ORDER MANAGEMENT</h2>
          
          <div className="admin-header-actions" style={{ 
            marginBottom: '2rem',
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <button 
              className="print-btn"
              onClick={() => printDoorList(orders)}
              style={{
                backgroundColor: '#191919',
                color: 'white',
                padding: '10px 20px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              PRINT ALL EVENTS DOOR LIST
            </button>
            <button 
              className="print-btn"
              onClick={() => printShopOrders(orders)}
              style={{
                backgroundColor: '#191919',
                color: 'white',
                padding: '10px 20px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              PRINT SHOP PACKING LIST
            </button>
          </div>

          {/* Individual Event Print Buttons */}
          {(() => {
            // Group orders by event for individual printing
            const ordersByEvent = {};
            orders.forEach(order => {
              if (order.items) {
                order.items.forEach(item => {
                  if (item.eventId || item.isEvent) {
                    const eventName = item.name || 'Unknown Event';
                    if (!ordersByEvent[eventName]) {
                      ordersByEvent[eventName] = [];
                    }
                    // Add entry for each quantity
                    for (let i = 0; i < (item.quantity || 1); i++) {
                      ordersByEvent[eventName].push({
                        customerName: order.customerName || order.customerEmail || 'Unknown',
                        orderNumber: order.orderNumber || 'N/A',
                        ticketQuantity: item.quantity || 1,
                        price: item.totalPrice || item.price || 0,
                        isMember: order.isMemberOrder || false
                      });
                    }
                  }
                });
              }
            });

            return Object.keys(ordersByEvent).length > 0 && (
              <div style={{
                marginBottom: '2rem',
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '5px'
              }}>
                <h3 style={{ marginBottom: '1rem', color: '#191919' }}>Print Individual Event Lists:</h3>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {Object.entries(ordersByEvent).map(([eventName, attendees]) => (
                    <button
                      key={eventName}
                      onClick={() => printSingleEventDoorList(eventName, attendees)}
                      style={{
                        backgroundColor: '#fff',
                        color: '#191919',
                        padding: '8px 15px',
                        border: '2px solid #191919',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        borderRadius: '3px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#191919';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#fff';
                        e.target.style.color = '#191919';
                      }}
                    >
                      {eventName} ({attendees.length})
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
          
          <div className="admin-list">
            <h3>RECENT ORDERS ({orders.length})</h3>
            {orders.length === 0 ? (
              <p>No orders placed yet.</p>
            ) : (
              orders.map(order => {
                const ticketsArr = Array.isArray(order.generatedTickets) ? order.generatedTickets : [];
                const ticketCount = ticketsArr.length;
                const showTicketsSent = order.ticketEmailsSent === true;
                return (
                <div key={order.id} className="admin-item order-item">
                  <div className="order-header">
                    <h4>Order #{order.orderNumber}</h4>
                    {showTicketsSent && (
                      <span style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '6px',
                        marginLeft: '10px',
                        marginRight: '16px'
                      }}>
                        <span style={{
                          background: '#4caf50',
                          color: '#fff',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {`Tickets sent${ticketCount ? ` (${ticketCount})` : ''}`}
                        </span>
                        <button
                          onClick={() => handleResendTickets(order)}
                          disabled={resendingOrderId === order.id}
                          style={{
                            background: '#fff',
                            color: '#191919',
                            border: '1px solid #191919',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            cursor: resendingOrderId === order.id ? 'wait' : 'pointer',
                            opacity: resendingOrderId === order.id ? 0.6 : 1
                          }}
                        >
                          {resendingOrderId === order.id ? 'Re-sending…' : 'Re-send tickets'}
                        </button>
                      </span>
                    )}
                    <span className="order-date">
                      {/* Check for ticketsGeneratedAt first, then orderDate */}
                      {order.ticketsGeneratedAt && order.ticketsGeneratedAt.toDate ?
                        order.ticketsGeneratedAt.toDate().toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) :
                        order.orderDate && order.orderDate.toDate ?
                        order.orderDate.toDate().toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) :
                        'Date not available'
                      }
                    </span>
                  </div>
                  
                  <div className="order-details">
                    <div className="customer-info">
                      <p><strong>Customer:</strong> {order.customerName}</p>
                      <p><strong>Email:</strong> {order.customerEmail}</p>
                      <p><strong>Member Order:</strong> {order.isMemberOrder ? 'Yes' : 'No'}</p>
                    </div>
                    
                    <div className="shipping-info">
                      <p><strong>Shipping Address:</strong></p>
                      <p>{order.shippingAddress && order.shippingAddress.address}</p>
                      <p>{order.shippingAddress && order.shippingAddress.city}, {order.shippingAddress && order.shippingAddress.postalCode}</p>
                      <p>{order.shippingAddress && order.shippingAddress.country}</p>
                    </div>
                    {(order.dietaryNotes || order.notes) && (
                      <div className="order-notes" style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#fff3cd', borderRadius: '4px' }}>
                        <p><strong>Special Requirements:</strong> {order.dietaryNotes || order.notes}</p>
                      </div>
                    )}
                    
                    <div className="order-items">
                      <p><strong>Items:</strong></p>
                      {order.items && order.items.map((item, index) => (
                        <div key={index} className="order-item-detail">
                          <span>{item.name} x{item.quantity}</span>
                          <span>{item.totalPrice ? `${item.totalPrice.toFixed(2)} SEK` : ''}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="order-totals">
                      {order.isMemberOrder && order.originalTotal && order.memberDiscount && (
                        <>
                          <p>Original Total: {order.originalTotal.toFixed(2)} SEK</p>
                          <p>Member Discount: -{order.memberDiscount.toFixed(2)} SEK</p>
                        </>
                      )}
                      <p><strong>Final Total: {
                        order.finalTotal ? order.finalTotal.toFixed(2) : 
                        order.amount ? (order.amount / 100).toFixed(2) : 
                        order.items && order.items[0] && order.items[0].amount ? (order.items[0].amount / 100).toFixed(2) :
                        order.items ? order.items.reduce((sum, item) => sum + (item.totalPrice || 0), 0).toFixed(2) : 
                        '0.00'
                      } SEK</strong></p>
                    </div>
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {activeTab === 'contact' && (
        <div className="admin-section">
          <h2>CONTACT MESSAGES</h2>

          <div className="admin-form">
            <div className="form-row">
              <label>Status filter:</label>
              <select
                value={contactFilter}
                onChange={(e) => setContactFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="new">New</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>

          <div className="admin-list">
            <h3>MESSAGES ({contactMessages.length})</h3>
            {renderContactMessages()}
          </div>
        </div>
      )}

      {activeTab === 'youtube' && (
        <div className="admin-section">
          <h2>MANAGE YOUTUBE VIDEOS</h2>
          
          <div className="admin-form">
            <h3>ADD YOUTUBE VIDEO</h3>
            <form onSubmit={handleYouTubeSubmit}>
              <div className="form-row">
                <input
                  type="url"
                  placeholder="YouTube Video URL"
                  value={youtubeForm.url}
                  onChange={(e) => setYoutubeForm({...youtubeForm, url: e.target.value})}
                  required
                />
              </div>
              
              <input
                type="text"
                placeholder="Video Title (optional - for display)"
                value={youtubeForm.title}
                onChange={(e) => setYoutubeForm({...youtubeForm, title: e.target.value})}
              />
              
              <textarea
                placeholder="Video Description (optional)"
                value={youtubeForm.description}
                onChange={(e) => setYoutubeForm({...youtubeForm, description: e.target.value})}
              />
              
              <button type="submit" disabled={loading}>
                {loading ? 'ADDING...' : 'ADD VIDEO'}
              </button>
            </form>
          </div>

          <div className="admin-list">
            <h3>YOUTUBE VIDEOS ({youtubeVideos.length})</h3>
            {youtubeVideos.length === 0 ? (
              <p>No YouTube videos added yet.</p>
            ) : (
              youtubeVideos.map(video => (
                <div key={video.id} className="admin-item youtube-item">
                  <div className="youtube-thumbnail">
                    <img src={video.thumbnail} alt={video.title || 'YouTube Video'} />
                  </div>
                  <div className="item-info">
                    <h4>{video.title || 'YouTube Video'}</h4>
                    <p>{video.description}</p>
                    <p><strong>URL:</strong> <a href={video.url} target="_blank" rel="noopener noreferrer">{video.url}</a></p>
                    <p><strong>Video ID:</strong> {video.videoId}</p>
                    <p><strong>Added:</strong> {video.createdAt && video.createdAt.toDate ? video.createdAt.toDate().toLocaleDateString() : 'Unknown'}</p>
                  </div>
                  <button onClick={() => deleteYouTubeVideo(video.id)} className="delete-btn">
                    DELETE
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'carousel' && (
        <div className="admin-section">
          <h2>MANAGE CAROUSEL IMAGES</h2>
          <p>Upload images for the homepage carousel. Maximum 10 images allowed.</p>
          
          <div className="admin-form">
            <h3>ADD CAROUSEL IMAGE</h3>
            <form onSubmit={handleCarouselImageSubmit}>
              <div className="file-upload">
                <label>Upload image file:</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCarouselImageFile(e.target.files[0])}
                  required
                />
                {carouselImageFile && <span>Selected: {carouselImageFile.name}</span>}
                {uploadingCarouselImage && <span>Uploading...</span>}
              </div>
              
              <button type="submit" disabled={loading || uploadingCarouselImage || carouselImages.length >= 10}>
                {loading ? 'UPLOADING...' : carouselImages.length >= 10 ? 'MAXIMUM REACHED (10/10)' : `ADD IMAGE (${carouselImages.length}/10)`}
              </button>
            </form>
          </div>

          <div className="admin-list">
            <h3>CAROUSEL IMAGES ({carouselImages.length}/10)</h3>
            {carouselImages.length === 0 ? (
              <p>No carousel images uploaded yet.</p>
            ) : (
              <div className="carousel-images-grid">
                {carouselImages.map((image, index) => (
                  <div key={image.id} className="admin-item carousel-item">
                    <div className="carousel-image-preview">
                      <img src={image.imageUrl} alt={`Carousel ${index + 1}`} />
                      <div className="image-overlay">
                        <span className="image-number">#{index + 1}</span>
                      </div>
                    </div>
                    <div className="item-info">
                      <p><strong>Position:</strong> {index + 1}</p>
                      <p><strong>Added:</strong> {image.createdAt && image.createdAt.toDate ? image.createdAt.toDate().toLocaleDateString() : 'Unknown'}</p>
                    </div>
                    <button onClick={() => deleteCarouselImage(image.id)} className="delete-btn">
                      DELETE
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'calendar' && (
        <div className="admin-section">
          <h2>STUDIO CALENDAR MANAGEMENT</h2>
          <p>Calendar management functionality will be implemented here.</p>
          <p>This will allow blocking dates for studio bookings.</p>
        </div>
      )}

      {activeTab === 'admins' && (
        <div className="admin-section">
          <h2>MANAGE ADMIN USERS</h2>
          
          <div className="admin-form">
            <h3>CREATE NEW ADMIN USER</h3>
            <form onSubmit={handleAdminUserSubmit}>
              <div className="form-row">
                <input
                  type="email"
                  placeholder="Admin Email"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({...adminForm, email: e.target.value})}
                  required
                />
                <input
                  type="password"
                  placeholder="Password (min 6 characters)"
                  value={adminForm.password}
                  onChange={(e) => setAdminForm({...adminForm, password: e.target.value})}
                  minLength="6"
                  required
                />
              </div>
              
              <button type="submit" disabled={loading}>
                {loading ? 'CREATING...' : 'CREATE ADMIN USER'}
              </button>
            </form>
          </div>

          <div className="admin-list">
            <h3>EXISTING ADMIN USERS ({adminUsers.length})</h3>
            {adminUsers.length === 0 ? (
              <p>No admin users found.</p>
            ) : (
              adminUsers.map(admin => (
                <div key={admin.id} className="admin-item">
                  <div className="item-info">
                    <h4>{admin.email}</h4>
                    <p>Created: {admin.createdAt && admin.createdAt.toDate ? 
                      admin.createdAt.toDate().toLocaleDateString() : 
                      'Unknown'}</p>
                    {admin.id === currentUser.uid && <p><strong>(You)</strong></p>}
                  </div>
                  {admin.id !== currentUser.uid && (
                    <button 
                      onClick={() => removeAdminRole(admin.id, admin.email)} 
                      className="delete-btn"
                    >
                      REMOVE ADMIN
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'invoicing' && (
        <div className="admin-section">
          <Suspense fallback={<div>Loading invoicing…</div>}>
            <InvoiceGenerator />
          </Suspense>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
